import React, { useContext, useState, useEffect, useCallback } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { Link } from "react-router-dom";
import { Row, Col, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import { FloatCardComponent } from "../../../components/cards";
import ProductTable from "./ProductTable"
// import IssuerProductsTableComponent from "../../../components/tables/products/IssuerProductsTableComponent";
// import IssuerProductsTableComponent from "../../components/tables/products/IssuerProductsTableComponent";
import LabelFieldComponent from "../../../components/fields/LabelFieldComponent";
import PageLayout from "../../../layouts/PageLayout";

export default function AllProduct() {
  const { t, n } = useContext(TranslatorContext);

  // -----------------------------
  // State
  // -----------------------------
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [allProducts, setAllProducts] = useState([]);
  const [productsList, setProductsList] = useState([]);

  const [listers, setListers] = useState([]);

  const [selectedLister, setSelectedLister] = useState("");
  const [selectedRating, setSelectedRating] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");

  const [stats, setStats] = useState({
    total_products: 0,
    total_categories: 0,
    total_brands: 0,
  });

  const [tableHeaders] = useState([
   
    "Product Code",
    "Title",
    "Category",
    "Brand",
    "Selling Style",
    "Buying Price",
    "Market Places",
    "Action",
  ]);

  // -----------------------------
  // Fetch all products
  // -----------------------------
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/issuer/products`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      const resData = response.data;

      if (!resData.success) {
        setError(resData.message || "Failed to load products.");
        return;
      }

      const listerData = resData.data || [];

      // -----------------------------
      // Get listers
      // -----------------------------
      const listerOptions = listerData.map((item) => ({
        id: item.lister?.id,
        name: item.lister?.name,
      }));

      setListers(listerOptions);

      // -----------------------------
      // Flatten all lister products
      // -----------------------------
      const flattenedProducts = [];

      listerData.forEach((item) => {
        const lister = item.lister;

        (item.lister_products || []).forEach((product) => {
          flattenedProducts.push({
            ...product,

            // Keep lister information with every product
            lister_id: lister?.id,
            lister_name: lister?.name,
          });
        });
      });

      setAllProducts(flattenedProducts);
      setProductsList(flattenedProducts);

      // -----------------------------
      // Calculate statistics
      // -----------------------------
      const categoryIds = new Set();
      const brandIds = new Set();

      flattenedProducts.forEach((product) => {
        if (product.category?.id) {
          categoryIds.add(product.category.id);
        }

        if (product.brand?.id) {
          brandIds.add(product.brand.id);
        }
      });

      setStats({
        total_products: flattenedProducts.length,
        total_categories: categoryIds.size,
        total_brands: brandIds.size,
      });
    } catch (err) {
      console.error("Error fetching products:", err);

      setError(
        err.response?.data?.message ||
          "Failed to fetch products. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // -----------------------------
  // Initial API call
  // -----------------------------
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // -----------------------------
  // Filter Products
  // -----------------------------
  useEffect(() => {
    let filteredProducts = [...allProducts];

    // Lister filter
    if (selectedLister) {
      filteredProducts = filteredProducts.filter(
        (product) =>
          String(product.lister_id) === String(selectedLister)
      );
    }

    // Category filter
    if (selectedCategory) {
      filteredProducts = filteredProducts.filter(
        (product) =>
          String(product.category?.id) === String(selectedCategory)
      );
    }

    // Brand filter
    if (selectedBrand) {
      filteredProducts = filteredProducts.filter(
        (product) =>
          String(product.brand?.id) === String(selectedBrand)
      );
    }

    // Rating filter
    if (selectedRating) {
      const rating = parseInt(
        selectedRating.replace(/\D/g, ""),
        10
      );

      if (!isNaN(rating)) {
        filteredProducts = filteredProducts.filter(
          (product) => Number(product.rating) === rating
        );
      }
    }

    setProductsList(filteredProducts);
  }, [
    allProducts,
    selectedLister,
    selectedCategory,
    selectedBrand,
    selectedRating,
  ]);

  // -----------------------------
  // Get unique categories
  // -----------------------------
  const categories = [
    ...new Map(
      allProducts
        .filter((product) => product.category)
        .map((product) => [
          product.category.id,
          product.category,
        ])
    ).values(),
  ];

  // -----------------------------
  // Get unique brands
  // -----------------------------
  const brands = [
    ...new Map(
      allProducts
        .filter((product) => product.brand)
        .map((product) => [
          product.brand.id,
          product.brand,
        ])
    ).values(),
  ];

  // -----------------------------
  // Float Cards
  // -----------------------------
  const floats = [
    {
      title: "total_products",
      digit: stats.total_products,
      icon: "shopping_bag",
      variant: "lg blue",
    },
    {
      title: "total_categories",
      digit: stats.total_categories,
      icon: "widgets",
      variant: "lg green",
    },
    {
      title: "total_brands",
      digit: stats.total_brands,
      icon: "verified_user",
      variant: "lg purple",
    },
  ];

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <PageLayout>
      <Row>
        {/* Breadcrumb */}
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">
                {t("product_list")}
              </h3>

              <ul className="mc-breadcrumb-list">
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">
                    {t("home")}
                  </Link>
                </li>

                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">
                    {t("products")}
                  </Link>
                </li>

                <li className="mc-breadcrumb-item">
                  {t("product_list")}
                </li>
              </ul>
            </div>
          </div>
        </Col>

        {/* Main Content */}
        <Col xl={12}>
          <div className="mc-card">

            {error && (
              <Alert variant="danger" className="mb-4">
                {error}
              </Alert>
            )}

            {/* Filters */}
            <Row>

              {/* Lister */}
              <Col xs={12} sm={6} md={4} lg={3}>
                <LabelFieldComponent
                  label={t("lister_by")}
                  option={[
                    "All Listers",
                    ...listers.map((lister) => lister.name),
                  ]}
                  labelDir="label-col"
                  fieldSize="w-100 h-md mb-4"
                  onChange={(e) => {
                    const value = e.target.value;

                    if (value === "All Listers") {
                      setSelectedLister("");
                      return;
                    }

                    const selected = listers.find(
                      (lister) => lister.name === value
                    );

                    setSelectedLister(selected?.id || "");
                  }}
                />
              </Col>

              {/* Category */}
              <Col xs={12} sm={6} md={4} lg={3}>
                <LabelFieldComponent
                  label={t("category_by")}
                  option={[
                    "All Categories",
                    ...categories.map((category) => category.name),
                  ]}
                  labelDir="label-col"
                  fieldSize="w-100 h-md mb-4"
                  onChange={(e) => {
                    const value = e.target.value;

                    if (value === "All Categories") {
                      setSelectedCategory("");
                      return;
                    }

                    const selected = categories.find(
                      (category) => category.name === value
                    );

                    setSelectedCategory(selected?.id || "");
                  }}
                />
              </Col>

              {/* Brand */}
              <Col xs={12} sm={6} md={4} lg={3}>
                <LabelFieldComponent
                  label={t("brand_by")}
                  option={[
                    "All Brands",
                    ...brands.map((brand) => brand.name),
                  ]}
                  labelDir="label-col"
                  fieldSize="w-100 h-md mb-4"
                  onChange={(e) => {
                    const value = e.target.value;

                    if (value === "All Brands") {
                      setSelectedBrand("");
                      return;
                    }

                    const selected = brands.find(
                      (brand) => brand.name === value
                    );

                    setSelectedBrand(selected?.id || "");
                  }}
                />
              </Col>

              {/* Rating */}
              <Col xs={12} sm={6} md={4} lg={3}>
                <LabelFieldComponent
                  label={t("rating_by")}
                  option={[
                    "All Ratings",
                    "1 star",
                    "2 star",
                    "3 star",
                    "4 star",
                    "5 star",
                  ]}
                  labelDir="label-col"
                  fieldSize="w-100 h-md mb-4"
                  onChange={(e) => {
                    const value = e.target.value;

                    if (value === "All Ratings") {
                      setSelectedRating("");
                    } else {
                      setSelectedRating(value);
                    }
                  }}
                />
              </Col>

              {/* Table */}
              <Col xl={12}>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner
                      animation="border"
                      variant="primary"
                    />

                    <p className="mt-2 text-muted">
                      {t("loading_products...")}
                    </p>
                  </div>
                ) : (
                  <ProductTable
                    thead={tableHeaders}
                    tbody={productsList}
                  />
                )}
              </Col>

            </Row>
          </div>
        </Col>
      </Row>
    </PageLayout>
  );
}